import { useRef, type KeyboardEvent } from 'react';

export type UserDetailTab = 'profile' | 'audit';

interface UserDetailTabsProps {
  active: UserDetailTab;
  onChange: (tab: UserDetailTab) => void;
}

const TABS: { key: UserDetailTab; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'audit', label: 'Audit' },
];

/**
 * Profile / Audit segmented control inside the User Detail Pane. Spec:
 * `DetailPaneTabs.handoff.md`.
 *
 * Track: 3px padding + radius 9 + surface bg ("recessed rail").
 * Chip:  flex-1, h-30, radius 7, font-size 12. Concentric corners.
 *
 * Active state lifts the chip onto the pane surface (`--um-bg`) with a
 * two-layer box-shadow — soft drop (elevation) + 1px ring (hairline).
 * The ring is a `box-shadow`, NOT a `border`, so the active/inactive
 * states share the same box and the label doesn't shift 1px on toggle.
 *
 * Token mapping (Tailwind arbitrary values, theme-aware):
 *   --um-surface : #f7f8f7 / #1c2122   (track rail)
 *   --um-bg      : #ffffff / #15191a   (active chip fill = pane bg)
 *   --um-border  : #dce0dc / #2a3133   (active chip 1px ring)
 *   --um-ink     : bark    / #e9efe9   (active label)
 *   --um-muted   : muted               (inactive label)
 *
 * Accessibility: role="tablist" with arrow / Home / End keyboard nav.
 * Each tab carries `aria-controls` pointing at its panel (the consumer
 * gives the body container the matching id).
 */
export default function UserDetailTabs({ active, onChange }: UserDetailTabsProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKey(e: KeyboardEvent<HTMLDivElement>): void {
    const i = TABS.findIndex((t) => t.key === active);
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    else return;
    e.preventDefault();
    onChange(TABS[next].key);
    refs.current[next]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label="User detail sections"
      onKeyDown={onKey}
      className="mx-[18px] mb-3 flex gap-[3px] p-[3px] rounded-[9px] bg-[#f7f8f7] dark:bg-[#1c2122]"
    >
      {TABS.map((t, i) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`user-detail-tab-${t.key}`}
            aria-selected={isActive}
            aria-controls={`user-detail-panel-${t.key}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={
              isActive
                ? 'flex-1 h-[30px] rounded-[7px] border-0 text-[12px] font-bold cursor-pointer text-bark-900 dark:text-[#e9efe9] bg-white dark:bg-[#15191a] shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_1px_#dce0dc] dark:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_1px_#2a3133] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-moss-500'
                : 'flex-1 h-[30px] rounded-[7px] border-0 text-[12px] font-medium cursor-pointer text-stone-500 dark:text-stone-400 bg-transparent hover:text-bark-800 dark:hover:text-[#e8ece9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-moss-500'
            }
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
